import { NavLink, Outlet } from "react-router-dom";

import {
  useTemPermissao,
  useTemPermissaoIrrestrita,
} from "../../lib/auth/permissoes";

/*
 * Layout do módulo de Cadastros (FE-4 CP-B — ADR 0026).
 *
 * Estrutura: uma sub-nav (tabs) + o `<Outlet/>` da seção ativa. As tabs vivem num
 * array EXTENSÍVEL (`abas`): Empresas/Bancos (ação `cadastrar_infraestrutura`) e
 * Usuários/Perfis (ação `gerenciar_usuarios`). Cada aba carrega seu próprio
 * `visivel`, gated por permissão RBAC (a ação é a string EXATA do StrEnum do
 * backend). Só entram abas cuja rota já existe (nunca um link quebrado).
 *
 * Nota de gating: Empresas/Bancos aparecem a QUALQUER escopo da ação
 * (`useTemPermissao`) — ler o catálogo é permitido a usuário escopado; a ESCRITA
 * (criar/editar banco) exige escopo IRRESTRITO e é gated DENTRO da `PaginaBancos`.
 *
 * Já Usuários/Perfis são GLOBAL-ONLY: no backend a ação `gerenciar_usuarios` só
 * passa com escopo IRRESTRITO (rotas sem `empresa_id` no path → `exigir_permissao`
 * só libera irrestrito). Por isso o gate dessas abas é `useTemPermissaoIrrestrita`
 * — um usuário escopado-só veria abas "mortas" (403 em cada página) sem isso.
 *
 * SÓ classes Tailwind dos tokens CPMH — nenhum hex/px literal.
 */

/** Ação RBAC de empresas/bancos/contas — string EXATA do StrEnum do backend. */
const ACAO_CADASTRAR_INFRAESTRUTURA = "cadastrar_infraestrutura";
/** Ação RBAC de usuários/perfis — string EXATA do StrEnum do backend. */
const ACAO_GERENCIAR_USUARIOS = "gerenciar_usuarios";

interface AbaCadastro {
  rotulo: string;
  para: string;
  visivel: boolean;
}

export function CadastrosLayout(): JSX.Element {
  const podeInfraestrutura = useTemPermissao(ACAO_CADASTRAR_INFRAESTRUTURA);
  // Usuários/Perfis são global-only: exige escopo IRRESTRITO (ver nota acima).
  const podeGerenciarUsuarios = useTemPermissaoIrrestrita(
    ACAO_GERENCIAR_USUARIOS,
  );

  // Array extensível de tabs. Só entram abas cuja rota já existe (sem link
  // quebrado), gated por permissão da ação correspondente.
  const abas: AbaCadastro[] = [
    { rotulo: "Empresas", para: "empresas", visivel: podeInfraestrutura },
    { rotulo: "Bancos", para: "bancos", visivel: podeInfraestrutura },
    { rotulo: "Usuários", para: "usuarios", visivel: podeGerenciarUsuarios },
    { rotulo: "Perfis", para: "perfis", visivel: podeGerenciarUsuarios },
  ];

  return (
    <section className="flex flex-col gap-6">
      <h1 className="font-titulo text-2xl text-off-black">Cadastros</h1>

      <nav
        aria-label="Seções de cadastros"
        className="flex flex-wrap gap-2 border-b border-platinum"
      >
        {abas
          .filter((aba) => aba.visivel)
          .map((aba) => (
            <NavLink
              key={aba.para}
              to={aba.para}
              className={({ isActive }) =>
                isActive
                  ? "font-subtitulo text-base text-flame"
                  : "font-subtitulo text-base text-off-black hover:text-flame"
              }
            >
              {aba.rotulo}
            </NavLink>
          ))}
      </nav>

      <Outlet />
    </section>
  );
}
